import React, { useState, useMemo, useEffect } from 'react';
import SettingsIcon from '@material-ui/icons/Settings';
import {
  Tabs,
  Tab,
  Chip,
  Typography,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  ButtonGroup,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Badge,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import AddIcon from '@material-ui/icons/Add';
import DescriptionIcon from '@material-ui/icons/Description';
import NotificationImportantIcon from '@material-ui/icons/NotificationImportant';
import ErrorIcon from '@material-ui/icons/Error';
import WarningIcon from '@material-ui/icons/Warning';
import { makeStyles } from '@material-ui/core/styles';
import getStageColor from './data/StageColors';
import ContactsListView from './Components/ContactListView';
import ContactFormDialog from './Components/ContactFormDialog';
import useContactsOverviewController from './CustomHooks/useContactController';
import StageManagerDialog from './Components/StageManagerDialog';
import EnquiryFormModal from './EnquiryForm/EnquiryForm';
import ContactNotesDrawer from './Components/Notes/ContactNotesDrawer';

const INACTIVE_TAB_COLOR = '#bdbdbd';

const COLORS = {
  green900: '#123524',
  green700: '#1f5c3c',
  green600: '#2c7a4d',
  greenBg: '#f0f6f2',
  ink900: '#1a1d1f',
  ink600: '#5a6268',
  ink400: '#8a9296',
  line: '#e4e7e6',
  lineSoft: '#eef1f0',
  amber: '#b4720b',
  red: '#b8342c',
};

const useStyles = makeStyles(() => ({
  headerWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 4px 12px',
    marginBottom: 8,
    borderBottom: `1px solid ${COLORS.line}`,
    flexWrap: 'wrap',
    gap: 12,
  },
  headerSubtitle: {
    fontSize: '0.8rem',
    color: COLORS.ink400,
    marginTop: 2,
    fontWeight: 500,
  },
  controlsWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  searchInput: {
    width: 210,
    backgroundColor: '#fff',
    '& .MuiOutlinedInput-root': {
      height: 34,
      borderRadius: 6,
      fontSize: '0.8rem',
      backgroundColor: '#fff',
      '& fieldset': {
        borderColor: COLORS.line,
      },
      '&:hover fieldset': {
        borderColor: COLORS.ink400,
      },
      '&.Mui-focused fieldset': {
        borderColor: COLORS.green700,
        borderWidth: 1,
      },
    },
    '& .MuiOutlinedInput-input': {
      padding: '6px 10px 6px 0',
      color: COLORS.ink900,
    },
  },
  addBtn: {
    textTransform: 'none',
    fontWeight: 600,
    height: 34,
    fontSize: '0.78rem',
    backgroundColor: COLORS.green700,
    color: '#fff',
    borderRadius: 6,
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: COLORS.green900,
      boxShadow: 'none',
    },
  },
  groupBtn: {
    textTransform: 'none',
    fontWeight: 600,
    height: 34,
    fontSize: '0.78rem',
    borderColor: COLORS.line,
    color: COLORS.ink600,
    backgroundColor: '#fff',
    '&:hover': {
      backgroundColor: COLORS.greenBg,
      borderColor: COLORS.line,
      color: COLORS.green700,
    },
  },
  criticalBadge: {
    '& .MuiBadge-badge': {
      right: -2,
      top: -4,
      backgroundColor: COLORS.red,
      color: '#FFF',
      fontSize: '0.68rem',
      height: 16,
      minWidth: 16,
      padding: '0 4px',
    },
  },
  tabsRoot: {
    borderBottom: `1px solid ${COLORS.line}`,
    backgroundColor: '#fff',
    minHeight: 38,
  },
  tab: {
    minWidth: 120,
    minHeight: 38,
    padding: '6px 12px',
    fontWeight: 600,
    textTransform: 'none',
    fontSize: '0.82rem',
  },
  dot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    marginRight: 6,
    flexShrink: 0,
  },
  badge: {
    marginLeft: 6,
    height: 18,
    fontSize: '0.68rem',
    fontWeight: 700,
    color: '#fff',
  },
  criticalPopoverHeader: {
    padding: '10px 16px',
    fontWeight: 700,
    fontSize: '0.82rem',
    color: COLORS.ink900,
  },
  criticalList: {
    width: 320,
    maxHeight: 380,
    overflowY: 'auto',
    padding: 0,
  },
  criticalEmpty: {
    padding: 24,
    textAlign: 'center',
  },
}));

const ContactsOverview = props => {
  const classes = useStyles();
  const {
    Teams,
    TeamMembers,
    selectedTeam,
    teamWiseTabs,
    value,
    setValue,
    data,
    searchData,
    searchKey,
    searchCRMData,
    clearSearchData,
    inputRef,
    crmDataLoading,
    getFilterTabData,
    handleExportToExcel,
    addNewContact,
    getFollowUpStatus,
    criticalContacts,
    updateContactStage,
    // Single-contact delete wrapper
    handleDeleteContact,
    // Stage management
    addStage,
    editStageName,
    deleteStage,
    // Add Enquiry
    handleOpenAddEnquiry,
    openEnquiryForm,
    handleCloseAddEnquiry,
    handleEnquiryformSubmit,
    // Add menu
    addMenuOpen,
    setAddMenuOpen,
    addMenuAnchorRef,
    stageDialogOpen,
    setStageDialogOpen,

    notesState,
    getContactNotes,
    addContactNote,
    deleteContactNote,

    notesDrawer,
    handleOpenNotes,
    handleCloseNotes,
    userDetails,

    getEnquiryDetails,

    // Documents (fixed: EnquiryFormModal no longer manages these itself —
    // it always needs a real, current crmId, which only the hook knows for
    // certain once a contact has actually been created/selected)
    docsState,
    getContactDocs,
    uploadContactDocs,
    deleteContactDoc,
    downloadContactDoc,

    getParam, setParam, deleteParam,

    handleTimberSaleSubmit,
    getTimberSales,
    deleteTimberSaleRow,
    salesReferenceData
  } = useContactsOverviewController();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [criticalAnchorEl, setCriticalAnchorEl] = useState(null);

  const [hasDraft, setHasDraft] = useState(false);

  useEffect(() => {
    const checkDraft = () => {
      setHasDraft(!!localStorage.getItem('timber_enquiry_draft'));
    };
    checkDraft();

    // Listen for events from the modal or other tabs
    window.addEventListener('timberDraftChanged', checkDraft);
    window.addEventListener('storage', checkDraft);

    return () => {
      window.removeEventListener('timberDraftChanged', checkDraft);
      window.removeEventListener('storage', checkDraft);
    };
  }, []);

  const handleClearDraft = () => {
    localStorage.removeItem('timber_enquiry_draft');
    setHasDraft(false);
    window.dispatchEvent(new Event('timberDraftChanged'));
  };

  const activeStageTab = teamWiseTabs.find(t => t.value === value);
  const activeTabLabel = activeStageTab?.label;

  const activeRows = useMemo(
    () => (activeTabLabel ? getFilterTabData(searchData, activeTabLabel) : []),
    [searchData, activeTabLabel, getFilterTabData],
  );

  const totalContacts = searchData.length;
  const criticalCount = criticalContacts.length;


  useEffect(() => {
    const crmIdFromURL = getParam('crmID');
    if (crmIdFromURL && !editingContact && activeRows.length > 0) {
      const contactToOpen = activeRows.find(contact => String(contact.crmId) === String(crmIdFromURL));
      if (contactToOpen) {
        handleOpenEdit(contactToOpen);
      }
    }
  }, [activeRows, getParam, editingContact]);

  const handleOpenAdd = () => {
    setEditingContact(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = row => {
    setEditingContact(row);
    getContactNotes(row.crmId, true);
    setParam('crmID', row.crmId);
    handleOpenAddEnquiry(); 
  };

  const handleOpenNewEnquiry = () => {
    setEditingContact(null);
    handleOpenAddEnquiry();
  };

  const handleCloseEnquiryForm = () => {
  handleCloseAddEnquiry();
  setEditingContact(null);
  deleteParam('crmID');
};

  const handleSaveContact = async (payload, isEdit) => {
    const endpoint = isEdit ? 'CRM/EditContactNew' : 'CRM/AddContact';
    // Only the legacy Add flow needs the null->0 coercion; EditContactNewModel's
    // nullable fields expect real null.
    await addNewContact(endpoint, payload, { coerceNullsForLegacyAdd: !isEdit });
  };

  const handleSaveEnquiry = async (payload) => {
    const endpoint = 'Enquiry/EditEnquiryDetailsNew';
    await handleEnquiryformSubmit(payload, endpoint);
  };

  const handleOpenCritical = event => setCriticalAnchorEl(event.currentTarget);
  const handleCloseCritical = () => setCriticalAnchorEl(null);

  const handleCriticalContactClick = row => {
    handleCloseCritical();
    handleOpenEdit(row);
  };

  return (
    <div className="app-wrapper contact-overview">

      {/* Draft Indicator */}
      {hasDraft && (
        <Box
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 14px',
            marginBottom: 12,
            backgroundColor: '#f0f6f2',
            border: `1px solid ${COLORS.line}`,
            borderRadius: 6,
            gap: 12,
          }}
        >
          <Box display="flex" alignItems="center" style={{ minWidth: 0 }}>
            <DescriptionIcon
              style={{
                fontSize: 20,
                color: COLORS.green700,
                marginRight: 10,
                flexShrink: 0,
              }}
            />

            <Box>
              <Typography
                style={{
                  fontSize: '0.82rem',
                  fontWeight: 700,
                  color: COLORS.ink900,
                }}
              >
                Timber enquiry draft saved
              </Typography>

              <Typography
                style={{
                  fontSize: '0.75rem',
                  color: COLORS.ink600,
                  marginTop: 2,
                }}
              >
                You have an unfinished enquiry. Continue where you left off.
              </Typography>
            </Box>
          </Box>

          <Box display="flex" alignItems="center" style={{ gap: 8, flexShrink: 0 }}>
            <Button
              size="small"
              onClick={handleClearDraft}
              style={{
                textTransform: 'none',
                fontSize: '0.75rem',
                color: COLORS.ink600,
              }}
            >
              Discard
            </Button>

            <Button
              size="small"
              variant="contained"
              disableElevation
              startIcon={<DescriptionIcon style={{ fontSize: 15 }} />}
              onClick={handleOpenNewEnquiry}
              style={{
                textTransform: 'none',
                fontSize: '0.75rem',
                fontWeight: 600,
                backgroundColor: COLORS.green700,
                color: '#fff',
                borderRadius: 5,
              }}
            >
              Continue draft
            </Button>
          </Box>
        </Box>
      )}

      <Box className={classes.headerWrap}>
        <Box>
          <h2 className="title mb-2 mb-sm-0" style={{ cursor: 'default', fontSize: '1.5rem' }}>
            Contacts
          </h2>
          <Typography className={classes.headerSubtitle}>
            {totalContacts} contact{totalContacts === 1 ? '' : 's'} across {teamWiseTabs.length} stage
            {teamWiseTabs.length === 1 ? '' : 's'}
          </Typography>
        </Box>

        <Box className={classes.controlsWrap}>
          {/* Search Input */}
          <TextField
            inputRef={inputRef}
            value={searchKey}
            onChange={searchCRMData}
            placeholder="Search contacts..."
            variant="outlined"
            size="small"
            className={classes.searchInput}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ fontSize: 16, color: COLORS.ink400 }} />
                </InputAdornment>
              ),
              endAdornment: searchKey ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={clearSearchData} style={{ padding: 2 }}>
                    <ClearIcon style={{ fontSize: 14, color: COLORS.ink400 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          <Box display="flex" alignItems="center" gridGap={8}>
            {/* Secondary Action: Add Contact */}
            {/* <Button
              variant="outlined"
              color="primary"
              className={classes.addBtn}
              size="small"
              startIcon={<AddIcon style={{ fontSize: 16 }} />}
              onClick={handleOpenAdd}
            >
              Add Contact
            </Button> */}

            {/* Primary Action: New Timber Enquiry */}
            <Button
              variant="contained"
              color="primary"
              className={classes.addBtn}
              size="small"
              disableElevation
              startIcon={<DescriptionIcon style={{ fontSize: 16 }} />}
              onClick={handleOpenNewEnquiry}
            >
              New Enquiry
            </Button>
          </Box>

          {/* Secondary Actions (Export & Critical) */}
          <ButtonGroup variant="outlined" aria-label="contact actions">
            <Button className={classes.groupBtn} onClick={handleOpenCritical}>
              <Badge
                className={classes.criticalBadge}
                badgeContent={criticalCount}
                max={99}
                invisible={criticalCount === 0}
              >
                <NotificationImportantIcon
                  style={{
                    fontSize: 16,
                    marginRight: criticalCount ? 6 : 0,
                    color: criticalCount > 0 ? COLORS.amber : COLORS.ink400,
                  }}
                />
              </Badge>
              Critical
            </Button>
          </ButtonGroup>
        </Box>
      </Box>

      <Box display="flex" alignItems="center">
        <Tabs
          value={value}
          onChange={(_, newValue) => setValue(newValue)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          className={classes.tabsRoot}>
          {teamWiseTabs.map((tab, index) => {
            const isActive = tab.value === value;
            const color = isActive ? getStageColor(tab.label, index) : INACTIVE_TAB_COLOR;
            const count = getFilterTabData(searchData, tab.label).length;
            return (
              <Tab
                key={tab.value}
                value={tab.value}
                className={classes.tab}
                style={{ color: isActive ? undefined : INACTIVE_TAB_COLOR }}
                label={
                  <span style={{ display: 'flex', alignItems: 'center' }}>
                    <span className={classes.dot} style={{ backgroundColor: color }} />
                    {tab.label}
                    {count > 0 && (
                      <Chip label={count} size="small" className={classes.badge} style={{ backgroundColor: color }} />
                    )}
                  </span>
                }
              />
            );
          })}
        </Tabs>
        {teamWiseTabs.length > 0 && (
          <IconButton size="small" onClick={() => setStageDialogOpen(true)} style={{ marginLeft: 8 }}>
            <SettingsIcon fontSize="small" />
          </IconButton>
        )}
      </Box>


      <div style={{ height: 'calc(100vh - 240px)', width: '100%', paddingTop: '0.75rem' }}>
        {teamWiseTabs.length === 0 ? (
          <Box display="flex" alignItems="center" justifyContent="center" height="100%">
            <Typography variant="body1" color="textSecondary">
              No stages set up for this team yet.
            </Typography>
          </Box>
        ) : (
          <ContactsListView
            rows={activeRows}
            loading={crmDataLoading}
            onRowClick={handleOpenEdit}
            getFollowUpStatus={getFollowUpStatus}
            stages={teamWiseTabs}
            getStageColor={getStageColor}
            onStageChange={updateContactStage}
            onDeleteContact={handleDeleteContact}
            onOpenNotes={handleOpenNotes}
          />
        )}

      </div>

      {/* <ContactFormDialog
        open={dialogOpen}
        onClose={handleCloseEdit}
        onFetchEnquiry={crmId => getEnquiryDetails(crmId)}
        onSaveContact={handleSaveContact}
        onSaveEnquiry={handleSaveEnquiry}
        onAddNote={(text, mentionedUsers) => addContactNote(editingContact, text, mentionedUsers)}
        onDeleteNote={note => deleteContactNote(editingContact, note)}
        notes={notesState[editingContact?.crmId]?.items || []}
        loading={notesState[editingContact?.crmId]?.loading || false}
        contactData={editingContact}
        currentUserId={userDetails?.userId}
        currentUserEmail={userDetails?.emailAddress}
        currentStage={activeStageTab}
        teams={Teams}
        stages={teamWiseTabs}
        teamMembers={TeamMembers}
        selectedTeam={selectedTeam}
      /> */}

      <StageManagerDialog
        open={stageDialogOpen}
        onClose={() => setStageDialogOpen(false)}
        stages={teamWiseTabs}
        getStageColor={getStageColor}
        getContactCountForStage={label => getFilterTabData(searchData, label).length}
        onAddStage={addStage}
        onEditStage={editStageName}
        onDeleteStage={deleteStage}
      />

      <ContactNotesDrawer
        open={notesDrawer.open}
        onClose={handleCloseNotes}
        contact={notesDrawer.row}
        notes={notesState[notesDrawer.row?.crmId]?.items || []}
        loading={notesState[notesDrawer.row?.crmId]?.loading || false}
        onAddNote={text => addContactNote(notesDrawer.row, text)}
        onDeleteNote={note => deleteContactNote(notesDrawer.row, note)}
        currentUserId={userDetails?.userId}
      />

      <EnquiryFormModal
        open={openEnquiryForm}
        onClose={handleCloseEnquiryForm}
        editingContact={editingContact}
        handleEnquiryformSubmit={handleEnquiryformSubmit}
        getEnquiryDetails={getEnquiryDetails}
        contacts={data}
        selectedTeam={selectedTeam}
        docsState={docsState}
        getContactDocs={getContactDocs}
        uploadContactDocs={uploadContactDocs}
        deleteContactDoc={deleteContactDoc}
        downloadContactDoc={downloadContactDoc}
        currentStage={activeStageTab}
        teams={Teams}
        stages={teamWiseTabs}
        teamMembers={TeamMembers}
        handleTimberSaleSubmit={handleTimberSaleSubmit}
        getTimberSales={getTimberSales}
        deleteTimberSaleRow={deleteTimberSaleRow}
        salesReferenceData={salesReferenceData}
        notesState={notesState}
        getContactNotes={getContactNotes}
        addContactNote={addContactNote}
        currentUserId={userDetails?.userId}
        getFollowUpStatus={getFollowUpStatus}
      />

      <Popover
        open={Boolean(criticalAnchorEl)}
        anchorEl={criticalAnchorEl}
        onClose={handleCloseCritical}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Typography className={classes.criticalPopoverHeader}>
          Critical Contacts {criticalCount > 0 ? `(${criticalCount})` : ''}
        </Typography>
        <Divider />
        {criticalContacts.length === 0 ? (
          <Box className={classes.criticalEmpty}>
            <Typography variant="body2" color="textSecondary">
              Nothing overdue or due soon. You're all caught up.
            </Typography>
          </Box>
        ) : (
          <List className={classes.criticalList} dense>
            {criticalContacts.map(row => {
              const status = getFollowUpStatus(row);
              const isOverdue = status === 'overdue';
              const color = isOverdue ? '#d32f2f' : '#f57c00';
              return (
                <ListItem key={row.crmId} button onClick={() => handleCriticalContactClick(row)}>
                  <ListItemIcon style={{ minWidth: 32 }}>
                    {isOverdue ? (
                      <ErrorIcon fontSize="small" style={{ color }} />
                    ) : (
                      <WarningIcon fontSize="small" style={{ color }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={row.FullName}
                    primaryTypographyProps={{ style: { fontSize: '0.85rem', fontWeight: 600 } }}
                    secondary={`${row.crmStageName || '-'} · Follow up ${row.localFollowUpDate || '-'}${isOverdue ? ' · Overdue' : ' · Due soon'
                      }`}
                    secondaryTypographyProps={{ style: { fontSize: '0.75rem', color } }}
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </Popover>


    </div >
  );
};

export default ContactsOverview;